import { create } from 'zustand';

/**
 * 認証タイプ
 */
export type AuthType = 'refresh' | 'aal2';

/**
 * 認証キャンセルエラー
 * ユーザーが認証をキャンセルした場合に投げられるエラー
 */
export class AuthCancelledError extends Error {
  constructor() {
    super('AUTH_CANCELLED');
    this.name = 'AuthCancelledError';
  }
}

/**
 * GlobalAuthModalの状態管理
 * Promiseベースで認証完了を待機できるように実装
 */
type GlobalAuthModalState = {
  /**
   * モーダルの開閉状態
   */
  isOpen: boolean;
  /**
   * 認証タイプ（refresh: パスワード再入力、aal2: MFA）
   */
  authType: AuthType;
  /**
   * モーダルに表示するメッセージ（BE の displayMessage 等）。
   * 未指定時は各モーダルの既定文言にフォールバックする。
   */
  message?: string;
  /**
   * 認証成功時に解決するPromiseのresolver
   */
  _resolver: (() => void) | null;
  /**
   * 認証キャンセル/失敗時にrejectするPromiseのrejecter
   */
  _rejecter: ((error: Error) => void) | null;
  /**
   * モーダルを開く（Promiseを返す）
   * @param authType - 認証タイプ（デフォルト: "refresh"）
   * @param options.message - モーダルに表示するメッセージ（任意）
   * @returns 認証完了時に解決するPromise
   */
  openModal: (
    authType?: AuthType,
    options?: { message?: string },
  ) => Promise<void>;
  /**
   * 認証成功時に呼ぶ（Promiseを解決）
   */
  resolveAuth: () => void;
  /**
   * 認証キャンセル/失敗時に呼ぶ（Promiseをreject）
   */
  rejectAuth: (error?: Error) => void;
};

export const useGlobalAuthModal = create<GlobalAuthModalState>((set, get) => ({
  isOpen: false,
  authType: 'refresh',
  message: undefined,
  _resolver: null,
  _rejecter: null,

  openModal: (
    authType: AuthType = 'refresh',
    options?: { message?: string },
  ) => {
    return new Promise<void>((resolve, reject) => {
      set({
        isOpen: true,
        authType,
        message: options?.message,
        _resolver: resolve,
        _rejecter: reject,
      });
    });
  },

  resolveAuth: () => {
    const { _resolver } = get();
    if (_resolver) {
      _resolver();
    }
    set({
      isOpen: false,
      authType: 'refresh',
      message: undefined,
      _resolver: null,
      _rejecter: null,
    });
  },

  rejectAuth: (error?: Error) => {
    const { _rejecter } = get();
    if (_rejecter) {
      _rejecter(error ?? new Error('Authentication failed'));
    }
    set({
      isOpen: false,
      authType: 'refresh',
      message: undefined,
      _resolver: null,
      _rejecter: null,
    });
  },
}));
